import { useMemo } from "react";

import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { FormFrame, ButtonGroup, FormBox } from "@impero-com/ui";
import { useNavigate } from "react-router-dom";

import { UserOverview, CreateEntity } from "api/generated/model";
import { AccessRights, AccessRightsValue } from "components/accessRights";
import { StringInput } from "components/form/legacyFields/stringInput";
import { TextAreaInput } from "components/form/legacyFields/textAreaInput";
import { useCreateEntityMutation } from "org/entities/queries/useCreateEntityMutation";
import { ACCESS_RIGHTS } from "utils/accessRights/constants";
import {
    RiskEntityAccessRights,
    RiskEntityAccessRightsInstance,
} from "utils/accessRights/models";
import {
    FormCreate,
    FormRootComponentProps,
    hasError,
    subForm,
    ObjectError,
} from "utils/form";
import { useAvailableActions } from "utils/hooks/useAvailableActions";
import { useOrgUserProfileCookie } from "utils/profileCookie";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";
import { ENTITIES_HASH } from "../structure/manager";
import { RISK_MODULE } from "../url";

import { checkRemoteNameAvailability } from "./utils";

interface EntityCreatorValues {
    details: Details;
}

interface Details {
    name: string;
    description: string;
    accessRights: AccessRightsValue<RiskEntityAccessRights>;
}

type EntityCreatorFormProps = FormRootComponentProps<EntityCreatorValues>;

function EntityCreatorForm(props: EntityCreatorFormProps) {
    const { form, wrappedComponentRef } = props;

    const { t } = useTranslation();

    const availableActions = useAvailableActions();

    const userHasAccess = availableActions.risk.entity.create;

    const content = userHasAccess ? (
        <View form={form} wrappedComponentRef={wrappedComponentRef} />
    ) : (
        <NoAccessCard />
    );

    return (
        <OrgApplicationBar
            title={{
                title: t("RiskEntities_Create_New_Title"),
                breadCrumbs: [
                    {
                        label: t("RiskDirectory_RiskManager"),
                        url: RISK_MODULE,
                    },
                ],
            }}
        >
            {content}
        </OrgApplicationBar>
    );
}

function View(props: EntityCreatorFormProps) {
    const { form } = props;

    const { t } = useTranslation();

    const orgProfileCookie = useOrgUserProfileCookie();

    const detailForm = subForm("details", form);
    const resourceAccessRights = RiskEntityAccessRightsInstance;

    const { getFieldsError } = detailForm;

    let currentUser: UserOverview | undefined;
    if (orgProfileCookie) {
        const { userId, firstName, lastName, email } = orgProfileCookie;
        currentUser = {
            id: userId,
            firstname: firstName,
            lastname: lastName,
            email,
        };
    }

    const checkNameAvailability = useMemo(
        () =>
            async (name: string): Promise<void> => {
                await checkRemoteNameAvailability(
                    name,
                    t("Entities_Edit_Error_EntityAlreadyExists")
                );
            },
        [t]
    );

    const { mutateAsync, isPending } = useCreateEntityMutation();
    const navigate = useNavigate();

    const save = useMemo(
        () => () => {
            const { validateFields } = form;

            validateFields({ force: true }, (errors, values) => {
                if (errors) {
                    return;
                }
                const { name, description, accessRights } = values.details;

                const data: CreateEntity = {
                    name,
                    description,
                    responsibleId: accessRights.responsible!.id,
                    kind: "Risk",
                    controlUserIdAndRights: [],
                    riskUserIdAndRights: accessRights.usersAndRights.map(
                        ({ user, accessRight }) => ({
                            userId: user.id,
                            right: accessRight,
                        })
                    ),
                    tagIds: [],
                };

                mutateAsync({ data }).then(() => {
                    navigate(RISK_MODULE + ENTITIES_HASH);
                });
            });
        },
        [form, mutateAsync, navigate]
    );

    return (
        <FormFrame>
            <FormBox>
                <StringInput
                    type="CREATE"
                    id="name"
                    label={t("Entity_Label_Name")}
                    initialValue=""
                    validators={[
                        {
                            kind: "ruleset",
                            required: true,
                            whitespace: true,
                        },
                        {
                            kind: "async",
                            asyncValidator: checkNameAvailability,
                        },
                    ]}
                    form={detailForm}
                />
            </FormBox>
            <FormBox>
                <TextAreaInput
                    type="CREATE"
                    id="description"
                    label={t("RiskDirectory_Label_Description")}
                    initialValue=""
                    form={detailForm}
                />
            </FormBox>
            <FormBox>
                <AccessRights
                    type="CREATE"
                    id="accessRights"
                    initialValue={{
                        responsible: currentUser,
                        usersAndRights: currentUser
                            ? [
                                  {
                                      user: currentUser,
                                      accessRight: ACCESS_RIGHTS.ADMIN,
                                  },
                              ]
                            : [],
                    }}
                    form={detailForm}
                    resourceAccessRights={resourceAccessRights}
                />
            </FormBox>
            <ButtonGroup
                buttonSpecs={[
                    {
                        id: "entity-creator-details-next",
                        label: t("RiskEntities_Create_Title"),
                        type: "primary",
                        disabled: hasError(getFieldsError() as ObjectError),
                        loading: isPending,
                        onClick: save,
                    },
                    {
                        id: "entity-creator-cancel",
                        label: t("Cancel"),
                        type: "default",
                        onClick: () => navigate(RISK_MODULE + ENTITIES_HASH),
                    },
                ]}
            />
        </FormFrame>
    );
}

export const EntityCreator = FormCreate<
    EntityCreatorValues,
    EntityCreatorFormProps
>()(EntityCreatorForm);
